// Pure URL builders + response parsing for the keyless PDOK BAG OGC API Features
// v2. Kept free of `fetch` so every branch is unit-testable; the async wrapper
// (which chains verblijfsobject -> pand) lives in fetchBag.js.
//
// Building facts live on two collections: the verblijfsobject (oppervlakte,
// gebruiksdoel, status) carries a link to its pand (oorspronkelijk bouwjaar).
// The join key is the BAG `adresseerbaarobject_id` stored on Adresses.Location.
//
// AI-CONTEXT: See docs/features/adress-external-data.md

const BAG_BASE = 'https://api.pdok.nl/kadaster/bag/ogc/v2'

const buildVerblijfsobjectUrl = (bagId) =>
    `${BAG_BASE}/collections/verblijfsobject/items?identificatie=${encodeURIComponent(bagId)}&f=json`

const buildPandUrl = (pandUuid) => `${BAG_BASE}/collections/pand/items/${pandUuid}?f=json`

// The verblijfsobject feature links its pand via `pand.href` — an array of full
// item URLs. Pull the trailing uuid off the first one.
const extractPandUuid = (vobProps) => {
    const href = vobProps && vobProps['pand.href']
    const url = Array.isArray(href) ? href[0] : href
    if (typeof url !== 'string') return ''
    const match = url.match(/\/pand\/items\/([^/?]+)/)
    return match ? match[1] : ''
}

const hasValue = (value) => {
    if (value === null || value === undefined) return false
    if (typeof value === 'string' && value.trim() === '') return false
    return true
}

const parseVerblijfsobject = (json) => {
    const feature = json && Array.isArray(json.features) ? json.features[0] : null
    const p = feature && feature.properties ? feature.properties : null
    if (!p) return null
    return {
        gebruiksdoel: Array.isArray(p.gebruiksdoel) ? p.gebruiksdoel.join(', ') : p.gebruiksdoel || '',
        oppervlakte: hasValue(p.oppervlakte) ? p.oppervlakte : null,
        status: p.status || '',
        pandUuid: extractPandUuid(p),
    }
}

const parsePand = (json) => {
    const p = json && json.properties ? json.properties : null
    if (!p) return null
    return { bouwjaar: hasValue(p.bouwjaar) ? p.bouwjaar : null, status: p.status || '' }
}

// `key` staat los van `label`: de kaart verdeelt deze feiten over secties, en
// dat mag niet op de zichtbare tekst gebeuren -- een hernoemd label zou het
// feit dan stil naar de verkeerde sectie verhuizen.
// AI-CONTEXT: See docs/pitfalls.md#database-rowtitle-join-strands-renamed-rows
// De standaardwaarde `= {}` dekt alleen `undefined`, niet `null` -- vandaar de
// eigen guard eromheen.
const selectBagFacts = (input) => {
    const { vob, pand } = input || {}
    const facts = []
    if (pand && hasValue(pand.bouwjaar)) facts.push({ key: 'bouwjaar', label: 'Bouwjaar', value: pand.bouwjaar, unit: 'year' })
    if (vob) {
        if (hasValue(vob.oppervlakte)) facts.push({ key: 'oppervlakte', label: 'Oppervlakte', value: vob.oppervlakte, unit: 'm²' })
        if (hasValue(vob.gebruiksdoel)) facts.push({ key: 'gebruiksdoel', label: 'Gebruiksdoel', value: vob.gebruiksdoel })
        if (hasValue(vob.status)) facts.push({ key: 'status', label: 'Status', value: vob.status })
    }
    return facts
}

export {
    BAG_BASE,
    buildVerblijfsobjectUrl,
    buildPandUrl,
    extractPandUuid,
    parseVerblijfsobject,
    parsePand,
    selectBagFacts,
}
